import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map, of } from 'rxjs';
import { environment } from '../environments/enviroment';
import { UserMindX } from '../models/mindx';

export interface AuthUserInput {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  document: string;
  documentType: string;
  phone: string;
  status: boolean;
  enableOTP: boolean;
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

interface FindAllUsersResponse {
  findAllUsers: {
    total: number;
    items: UserMindX[];
  };
}

export interface WorkspaceAssignmentInfo {
  id: string;
  userPlatformId: string;
  workspaceId: string;
  active: boolean;
  roleId?: string;
  roleName?: string;
  companyPlatformId?: string;
  companyId?: string;
  platformId?: string;
  userPlatformStatus?: boolean;
}

interface WorkspaceUserMindX extends UserMindX {
  workspaceAssignments?: WorkspaceAssignmentInfo[];
}

export interface FindUsersByWorkspaceFilters {
  workspaceId?: string | null;
  assignmentActive?: boolean;
  search?: string | null;
  skip?: number;
  take?: number;
  orderBy?: string | null;
  orderDirection?: string | null;
}

interface FindAllWorkspaceUsersResponse {
  findAllUsers: {
    total: number;
    items: WorkspaceUserMindX[];
  };
}

interface CreateUserResponse {
  createUser: UserMindX;
}

interface UpdateUserResponse {
  updateUser: UserMindX;
}

@Injectable({
  providedIn: 'root'
})
export class AuthUserSyncService {
  private readonly authGraphqlUrl = `${environment.apiUrl}auth/report/`;

  constructor(
    private http: HttpClient,
    private apollo: Apollo
  ) {}

  private buildHeaders(includeAuthorization = false, preferWorkspaceToken = false): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (includeAuthorization) {
      const authToken = preferWorkspaceToken
        ? (localStorage.getItem('token') || localStorage.getItem('token-otp') || localStorage.getItem('authToken'))
        : (localStorage.getItem('authToken') || localStorage.getItem('token'));
      if (authToken) {
        headers = headers.set('Authorization', `Bearer ${authToken}`);
      }
    }

    return headers;
  }

  private postGraphql<T>(query: string, variables: Record<string, unknown>, includeAuthorization = false): Observable<T> {
    const headers = this.buildHeaders(includeAuthorization);

    return this.http.post<GraphQLResponse<T>>(
      this.authGraphqlUrl,
      { query, variables },
      { headers }
    ).pipe(
      map((response) => {
        if (response.errors?.length) {
          throw new Error(response.errors[0].message || 'GraphQL request failed');
        }

        if (!response.data) {
          throw new Error('GraphQL response without data');
        }

        return response.data;
      })
    );
  }

  findUserByEmail(email: string): Observable<UserMindX | null> {
    const usersClient = this.apollo.use('users');
    const normalizedEmail = String(email || '').trim();
    if (!normalizedEmail) {
      return of(null);
    }

    const query = gql`
      query FindAllUsers(
        $search: String
        $skip: Int
        $take: Int
        $orderBy: UserOrderBy
        $orderDirection: OrderDirection
      ) {
        findAllUsers(
          search: $search
          skip: $skip
          take: $take
          orderBy: $orderBy
          orderDirection: $orderDirection
        ) {
          total
          items {
            id
            email
            firstName
            lastName
            document
            documentType
            phone
            status
            enableOTP
          }
        }
      }
    `;

    return usersClient.query<FindAllUsersResponse>({
      query,
      variables: {
        search: normalizedEmail,
        skip: 0,
        take: 20,
        orderBy: 'email',
        orderDirection: 'ASC'
      },
      context: {
        headers: this.getWorkspaceAuthorizationHeaders()
      },
      fetchPolicy: 'no-cache'
    }).pipe(
      map((result) => {
        const users = result.data?.findAllUsers?.items || [];
        return users.find((user) => String(user.email || '').toLowerCase() === normalizedEmail.toLowerCase()) || null;
      })
    );
  }

  findUserByEmailInWorkspace(
    email: string,
    workspaceId: string,
    assignmentActive = true
  ): Observable<UserMindX | null> {
    const normalizedEmail = String(email || '').trim();
    const normalizedWorkspaceId = String(workspaceId || '').trim();

    if (!normalizedEmail || !normalizedWorkspaceId) {
      return of(null);
    }

    return this.findUsersByWorkspace({
      workspaceId: normalizedWorkspaceId,
      assignmentActive,
      search: normalizedEmail,
      skip: 0,
      take: 20,
      orderBy: 'email',
      orderDirection: 'ASC'
    }).pipe(
      map((users) => (
        users.find((user) => String(user.email || '').toLowerCase() === normalizedEmail.toLowerCase()) || null
      ))
    );
  }

  findUsersByWorkspace(filters: FindUsersByWorkspaceFilters): Observable<WorkspaceUserMindX[]> {
    const normalizedWorkspaceId = String(filters.workspaceId || '').trim();

    if (!normalizedWorkspaceId) {
      return of([]);
    }

    const usersClient = this.apollo.use('users');
    const query = gql`
      query FindAllUsers(
        $workspaceId: ID
        $assignmentActive: Boolean
        $search: String
        $skip: Int
        $take: Int
        $orderBy: UserOrderBy
        $orderDirection: OrderDirection
      ) {
        findAllUsers(
          workspaceId: $workspaceId
          assignmentActive: $assignmentActive
          search: $search
          skip: $skip
          take: $take
          orderBy: $orderBy
          orderDirection: $orderDirection
        ) {
          total
          items {
            id
            email
            firstName
            lastName
            status
            enableOTP
            document
            workspaceAssignments {
              id
              userPlatformId
              workspaceId
              active
              roleId
              roleName
              companyPlatformId
              companyId
              platformId
              userPlatformStatus
            }
          }
        }
      }
    `;

    return usersClient.query<FindAllWorkspaceUsersResponse>({
      query,
      variables: {
        workspaceId: normalizedWorkspaceId,
        assignmentActive: filters.assignmentActive,
        search: filters.search?.trim() || undefined,
        skip: filters.skip,
        take: filters.take,
        orderBy: filters.orderBy || undefined,
        orderDirection: filters.orderDirection || undefined
      },
      context: {
        headers: this.getWorkspaceAuthorizationHeaders()
      },
      fetchPolicy: 'no-cache'
    }).pipe(
      map((result) => result.data?.findAllUsers?.items || [])
    );
  }

  private getWorkspaceAuthorizationHeaders(): Record<string, string> {
    const token = localStorage.getItem('token') || localStorage.getItem('token-otp') || localStorage.getItem('authToken') || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  createUser(input: AuthUserInput): Observable<UserMindX> {
    const usersClient = this.apollo.use('users');
    const mutation = gql`
      mutation CreateUser($input: CreateUserInput!) {
        createUser(input: $input) {
          id
          email
          firstName
          lastName
          status
          enableOTP
        }
      }
    `;

    return usersClient.mutate<CreateUserResponse>({
      mutation,
      variables: { input },
      context: {
        headers: this.getWorkspaceAuthorizationHeaders()
      }
    }).pipe(
      map((result) => {
        const created = result.data?.createUser;
        if (!created) {
          throw new Error('GraphQL response without created user');
        }
        return created;
      })
    );
  }

  updateUser(id: string, input: AuthUserInput): Observable<UserMindX> {
    const usersClient = this.apollo.use('users');
    const mutation = gql`
      mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
        updateUser(id: $id, input: $input) {
          id
          email
          firstName
          lastName
          status
          enableOTP
        }
      }
    `;

    return usersClient.mutate<UpdateUserResponse>({
      mutation,
      variables: { id, input },
      context: {
        headers: this.getWorkspaceAuthorizationHeaders()
      }
    }).pipe(
      map((result) => {
        const updated = result.data?.updateUser;
        if (!updated) {
          throw new Error('GraphQL response without updated user');
        }
        return updated;
      })
    );
  }
}
